// Studio entry. Wires Supabase client, auth, DB adapter, router, login form,
// and the new TypeScript views. Legacy view IIFEs (raw-viewer, calc-studio,
// bwm-studio, quality, export-center, settings, forms) read window.IFI.studio.*
// which we populate here.

import './main.css';
import { createSupa, type SupaWrapper } from './supabase-client';
import { createAuth, type AuthApi } from './auth';
import { createRemoteDb } from './db/remote';
import { bindDbAdapter } from './db/adapter';
import { migrateLocalToRemote, pullRemoteToLocal } from './db/migrate';
import { createAudit } from './audit';
import type { Role } from './db/types';
import { applyNavVisibility } from './ui/role-guard';
import { toast } from './ui/dom';
import { NAV_ITEMS, findNavItem, navTreeForRole, roleMatrix } from './navigation';
import { ensureViewLoaded } from './view-loader';
import { navTextOf, getLocale, setLocale } from './i18n';

const VIEW_ROLES: Record<string, Role[]> = roleMatrix();

declare global {
  interface Window {
    IFI?: {
      studio: {
        db?: unknown;
        dbLocal?: unknown;
        dbRemote?: unknown;
        STORAGE_MODE?: string;
        supa?: SupaWrapper;
        auth?: AuthApi;
        audit?: ReturnType<typeof createAudit>;
        migrate?: {
          migrate: typeof migrateLocalToRemote;
          pullFromSupabase: typeof pullRemoteToLocal;
        };
        adminUsers?: { init(): Promise<void> | void };
        rawDataManager?: { init(): Promise<void> | void };
        cviAikenPanel?: { init(): Promise<void> | void };
        triangulationMatrix?: { init(): Promise<void> | void };
        indicatorRegistry?: { init(): Promise<void> | void };
        bwmStudio?: { init(): Promise<void> | void };
        calcStudio?: { init(): Promise<void> | void };
        interviewForm?: { init(): Promise<void> | void };
        fgdForm?: { init(): Promise<void> | void };
        fieldForm?: { init(): Promise<void> | void };
        docForm?: { init(): Promise<void> | void };
        panduan?: { init(): Promise<void> | void };
        researchOverview?: { init(): Promise<void> | void };
        auditLogViewer?: { init(): Promise<void> | void };
        rawAssetDatabase?: { init(): Promise<void> | void };
        thematicCoding?: { init(): Promise<void> | void };
        researchWorkflowTracker?: { init(): Promise<void> | void };
        quality?: { init(): Promise<void> | void };
        exportCenter?: { init(): Promise<void> | void };
        settings?: { init(): Promise<void> | void };
        methodologyExplainer?: { init(): Promise<void> | void };
        dataCollectionHub?: { init(): Promise<void> | void };
        policyInsight?: { init(): Promise<void> | void };
        assetCaseReview?: { init(): Promise<void> | void };
        pocLocations?: { init(): Promise<void> | void };
        assignmentsAdmin?: { init(): Promise<void> | void };
        [k: string]: unknown;
      };
    };
  }
}

const supa = createSupa();
const auth = createAuth(supa);
const remote = createRemoteDb(supa);
const audit = createAudit(supa, auth);

window.IFI = window.IFI ?? { studio: {} };
window.IFI.studio = window.IFI.studio ?? {};
window.IFI.studio.supa = supa;
window.IFI.studio.auth = auth;
window.IFI.studio.audit = audit;
window.IFI.studio.dbRemote = remote;
// Per-view modules now lazy-loaded on first click via ensureViewLoaded()
// in openView(). See src/admin/view-loader.ts for the modKey → import map.

bindDbAdapter(remote, supa.storageMode);

// Expose migration helpers with legacy signature so settings.js keeps working.
interface LocalDbLike {
  getAll<T = unknown>(table: string): Promise<T[]>;
  bulkPut<T = unknown>(table: string, records: T[]): Promise<number>;
}
window.IFI.studio.migrate = {
  migrate: ((onLog: (m: string) => void, opts?: { dryRun?: boolean; tables?: string[] }) => {
    const local = window.IFI?.studio.dbLocal as LocalDbLike | undefined;
    if (!local) throw new Error('IndexedDB local tidak tersedia');
    return migrateLocalToRemote(local, remote, onLog, opts);
  }) as unknown as typeof migrateLocalToRemote,
  pullFromSupabase: ((onLog: (m: string) => void) => {
    const local = window.IFI?.studio.dbLocal as LocalDbLike | undefined;
    if (!local) throw new Error('IndexedDB local tidak tersedia');
    return pullRemoteToLocal(local, remote, onLog);
  }) as unknown as typeof pullRemoteToLocal,
};

// ============== LOGIN GATE ==============

function getEl<T extends HTMLElement = HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function showLogin(): void {
  const gate = getEl('loginGate');
  const app = getEl('studioApp');
  if (gate) gate.hidden = false;
  if (app) app.hidden = true;
  const issue = supa.configIssue;
  const missing = getEl('loginConfigMissing');
  if (missing && issue) {
    missing.hidden = false;
    missing.innerHTML = `<strong>Masalah konfigurasi:</strong> <code>${escapeHtml(issue)}</code>`;
  } else if (missing) {
    missing.hidden = true;
  }
  const badge = getEl('loginModeBadge');
  if (badge) badge.textContent = supa.configured ? 'Better Auth · password' : 'config missing';
  const label = getEl('loginModeLabel');
  if (label)
    label.textContent = supa.configured
      ? 'Better Auth · email + Google'
      : 'Konfigurasi belum lengkap';
}

async function showApp(): Promise<void> {
  const gate = getEl('loginGate');
  const app = getEl('studioApp');
  if (gate) gate.hidden = true;
  if (app) app.hidden = false;
  const profile = await auth.fetchProfile();
  const state = auth.getProfileState();
  const badge = getEl('sessionBadge');

  if (state && state.kind !== 'ok') {
    renderSidebar('viewer');
    renderProfileStateBanner(state);
    if (badge) badge.textContent = 'no role';
    applyNavVisibility(() => 'viewer', VIEW_ROLES);
    return;
  }

  const role = profile?.role ?? 'viewer';
  if (badge) badge.textContent = `${role}${profile?.full_name ? ' · ' + profile.full_name : ''}`;
  // Technical/infra banner (service_role, RLS, Edge Functions) is super_admin-only.
  const modeBanner = getEl('studioModeBanner');
  if (modeBanner) modeBanner.hidden = role !== 'super_admin';
  renderSidebar(role);
  applyNavVisibility(() => role, VIEW_ROLES);
  await bootstrap();
}

// ============== SIDEBAR RENDERING ==============

const STATUS_LABEL: Record<string, string> = {
  live: 'live',
  lokal: 'lokal',
  soon: 'soon',
  external: '↗',
};

function renderSidebar(role: Role): void {
  const nav = getEl('studioSidebarNav');
  if (!nav) return;
  const tree = navTreeForRole(role);
  nav.innerHTML = tree.map((entry) => {
    const items = entry.items.map((it) => {
      const label = navTextOf(it, 'label');
      const helper = navTextOf(it, 'helper');
      const tooltip = it.title ?? helper;
      const badge = it.status === 'live'
        ? ''
        : `<span class="nav-badge nav-badge--${it.status}">${escapeHtml(STATUS_LABEL[it.status] ?? it.status)}</span>`;
      const sensIcon = it.dataSensitivity === 'restricted' ? '🔒 ' : '';
      const labelHtml = `<span class="ico">${escapeHtml(it.icon)}</span>
        <span class="nav-text">${sensIcon}${escapeHtml(label)}</span>${badge}`;
      if (it.externalUrl) {
        return `<a class="studio-nav-item" href="${escapeHtml(it.externalUrl)}" target="_blank" rel="noopener" data-nav-id="${escapeHtml(it.id)}" title="${escapeHtml(tooltip)}">
          ${labelHtml}
        </a>`;
      }
      return `<button class="studio-nav-item" data-view="${escapeHtml(it.id)}" data-nav-id="${escapeHtml(it.id)}" title="${escapeHtml(tooltip)}">
        ${labelHtml}
      </button>`;
    }).join('');
    return `<div class="studio-nav-group">
      <div class="studio-nav-group__label">${escapeHtml(navTextOf(entry.group, 'label'))}</div>
      <div class="studio-nav-group__helper">${escapeHtml(navTextOf(entry.group, 'helper'))}</div>
      ${items}
    </div>`;
  }).join('');
}

function renderProfileStateBanner(state: NonNullable<ReturnType<AuthApi['getProfileState']>>): void {
  const main = getEl('studioMain');
  if (!main) return;
  let title = 'Akses bermasalah';
  let detail = '';
  if (state.kind === 'profile_missing') {
    title = 'Profil pengguna tidak ditemukan';
    detail =
      'Anda berhasil login, tetapi belum ada baris di <code>user_profiles</code> untuk akun ini. ' +
      'Hubungi <strong>super_admin</strong> untuk diberi peran.';
  } else if (state.kind === 'invalid_role') {
    title = 'Konfigurasi peran tidak valid';
    detail = `Nilai peran <code>${escapeHtml(state.raw)}</code> bukan salah satu dari super_admin / project_admin / source_person / viewer. Hubungi super_admin.`;
  } else if (state.kind === 'error') {
    title = 'Gagal memuat profil';
    detail = `Gagal membaca <code>user_profiles</code> dari Worker. Pesan: <code>${escapeHtml(state.message)}</code>`;
  }
  main.innerHTML = `
    <div class="view-help" style="background:rgba(204,102,119,0.1);border-left-color:var(--q-iif);">
      <strong>${escapeHtml(title)}.</strong> ${detail}
    </div>
  `;
}

function applyLoginI18n(lang: 'id' | 'en'): void {
  document.querySelectorAll<HTMLElement>('[data-i18n-id]').forEach((el) => {
    const val = lang === 'en' ? el.dataset.i18nEn : el.dataset.i18nId;
    if (val !== undefined) el.textContent = val;
  });
  document.querySelectorAll<HTMLInputElement>('[data-i18n-placeholder-id]').forEach((el) => {
    const val = lang === 'en' ? el.dataset.i18nPlaceholderEn : el.dataset.i18nPlaceholderId;
    if (val !== undefined) el.placeholder = val;
  });
  document.querySelectorAll<HTMLButtonElement>('#loginLangToggle [data-lang]').forEach((btn) => {
    btn.classList.toggle('login-lang-btn--active', btn.dataset.lang === lang);
  });
}

function wireLogin(): void {
  // Google login — button shown only when the Worker reports Google is configured.
  const googleBtn = getEl('loginGoogleBtn');
  googleBtn?.addEventListener('click', () => {
    void supa.signInGoogle().catch((e) => toast('Google login gagal: ' + (e as Error).message, 'err'));
  });
  void fetch('/api/config')
    .then((r) => r.json())
    .then((cfg: { google?: boolean }) => {
      if (cfg.google) {
        const wrap = getEl('loginGoogleWrap');
        if (wrap) wrap.hidden = false;
      }
    })
    .catch(() => {});

  // Login page EN/ID language toggle
  const loginLang = getLocale() as 'id' | 'en';
  applyLoginI18n(loginLang);
  getEl('loginLangToggle')?.querySelectorAll<HTMLButtonElement>('[data-lang]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = (btn.dataset.lang as 'id' | 'en') || 'id';
      setLocale(next);
      applyLoginI18n(next);
    });
  });

  // password login
  const form = getEl<HTMLFormElement>('loginPwForm');
  const err = getEl('loginPwError');
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    void (async () => {
      if (err) err.hidden = true;
      const data = new FormData(form);
      const email = String(data.get('email') ?? '').trim();
      const password = String(data.get('password') ?? '');
      try {
        await auth.signIn(email, password);
        await audit.logEvent('login', { mode: 'password' });
        await showApp();
      } catch (e2) {
        if (err) {
          err.hidden = false;
          err.textContent = (e2 as Error).message;
        }
      }
    })();
  });

  // sign-up toggle
  const tabLogin = getEl('loginTabLogin');
  const tabSignup = getEl('loginTabSignup');
  const blockLogin = getEl('loginBlockLogin');
  const blockSignup = getEl('loginBlockSignup');
  function setMode(mode: 'login' | 'signup') {
    if (!tabLogin || !tabSignup || !blockLogin || !blockSignup) return;
    tabLogin.classList.toggle('login-tab--active', mode === 'login');
    tabSignup.classList.toggle('login-tab--active', mode === 'signup');
    blockLogin.hidden = mode !== 'login';
    blockSignup.hidden = mode !== 'signup';
  }
  tabLogin?.addEventListener('click', () => setMode('login'));
  tabSignup?.addEventListener('click', () => setMode('signup'));

  const signupForm = getEl<HTMLFormElement>('loginSignupForm');
  const signupErr = getEl('loginSignupError');
  const signupOk = getEl('loginSignupOk');
  signupForm?.addEventListener('submit', (e) => {
    e.preventDefault();
    void (async () => {
      if (signupErr) signupErr.hidden = true;
      if (signupOk) signupOk.hidden = true;
      const data = new FormData(signupForm);
      const email = String(data.get('email') ?? '').trim();
      const password = String(data.get('password') ?? '');
      const fullName = String(data.get('full_name') ?? '').trim();
      if (password.length < 8) {
        if (signupErr) {
          signupErr.hidden = false;
          signupErr.textContent = 'Password minimum 8 karakter.';
        }
        return;
      }
      try {
        const r = await auth.signUp(email, password, fullName);
        if (r.needsConfirm) {
          if (signupOk) {
            signupOk.hidden = false;
            signupOk.textContent = 'Akun dibuat. Cek inbox untuk verifikasi email sebelum login.';
          }
        } else {
          await showApp();
        }
      } catch (e2) {
        if (signupErr) {
          signupErr.hidden = false;
          signupErr.textContent = (e2 as Error).message;
        }
      }
    })();
  });

  const resetLink = getEl('loginResetLink');
  resetLink?.addEventListener('click', (e) => {
    e.preventDefault();
    void (async () => {
      const email = window.prompt('Masukkan email Anda untuk menerima tautan reset password:');
      if (!email) return;
      try {
        await auth.resetPassword(email);
        toast('Tautan reset password dikirim ke ' + email + '.', 'ok');
      } catch (e2) {
        toast('Gagal: ' + (e2 as Error).message, 'err');
      }
    })();
  });

  const logoutBtn = getEl('logoutBtn');
  logoutBtn?.addEventListener('click', () => {
    void (async () => {
      await audit.logEvent('logout');
      await auth.logout();
      showLogin();
    })();
  });

  // Admin shell language toggle — shares IFI_LOCALE key with the public
  // dashboard, so a choice on either surface follows the user across both.
  // Reload after switch so NAV_EN sidebar overlay re-applies (matches the
  // existing Settings-page behavior from Phase F #3).
  const langToggle = document.getElementById('adminLangToggle');
  if (langToggle) {
    const currentLocale = getLocale();
    langToggle.querySelectorAll<HTMLButtonElement>('[data-lang]').forEach((btn) => {
      if (btn.dataset.lang === currentLocale) {
        btn.classList.add('lang-toggle__btn--active');
      }
      btn.addEventListener('click', () => {
        const next = (btn.dataset.lang as 'id' | 'en') || 'id';
        if (next === currentLocale) return;
        setLocale(next);
        location.reload();
      });
    });
  }

  // password-recovery deep link
  if (location.hash === '#reset') {
    const newPw = window.prompt('Masukkan password baru (min 8 karakter):');
    if (newPw && newPw.length >= 8) {
      void auth
        .updatePassword(newPw)
        .then(() => toast('Password diperbarui. Silakan login.', 'ok'))
        .catch((e) => toast('Gagal: ' + (e as Error).message, 'err'));
    }
  }

  auth.onAuthChange((evt) => {
    if (evt === 'SIGNED_IN') void showApp();
    if (evt === 'SIGNED_OUT') showLogin();
  });
}

// ============== ROUTER ==============

async function openView(name: string): Promise<void> {
  const item = findNavItem(name);
  if (!item || !item.viewId || !item.modKey) return;
  document.querySelectorAll<HTMLElement>('.studio-nav-item').forEach((b) => {
    b.classList.toggle('studio-nav-item--active', b.dataset.view === name);
  });
  const crumb = getEl('studioBreadcrumb');
  if (crumb) crumb.innerHTML = `Studio › <strong>${escapeHtml(item.label)}</strong>`;
  const main = getEl('studioMain');
  if (!main) return;
  const modKey = item.modKey;
  // Lazy-load the view chunk on first click (no-op if already loaded).
  main.innerHTML = '<div class="studio-loading" style="padding:24px;"><div class="spinner"></div><div>Memuat modul…</div></div>';
  const ok = await ensureViewLoaded(modKey, supa, auth);
  const mod = ok
    ? ((window.IFI?.studio as Record<string, unknown> | undefined)?.[modKey] as
        | { init?: () => Promise<void> | void }
        | undefined)
    : undefined;
  if (!mod || typeof mod.init !== 'function') {
    main.innerHTML = `<div class="view-help" style="background:rgba(204,102,119,0.1);border-left-color:var(--q-iif);">
      View module <code>${escapeHtml(modKey)}</code> gagal dimuat. Cek koneksi atau periksa <code>src/admin/view-loader.ts</code>.
    </div>`;
    return;
  }
  try {
    await mod.init();
  } catch (e) {
    const msg = (e as Error).message;
    main.innerHTML = `<div class="view-help" style="background:rgba(204,102,119,0.1);border-left-color:var(--q-iif);">
      <strong>Error:</strong> ${escapeHtml(msg)}
      <pre style="font-size:11px;white-space:pre-wrap;margin-top:8px;">${escapeHtml(((e as Error).stack ?? '').slice(0, 1500))}</pre>
    </div>`;
  }
  try {
    history.replaceState(null, '', '#' + name);
  } catch (_) {
    /* noop */
  }
}

function wireNav(): void {
  const nav = getEl('studioSidebarNav');
  if (!nav) return;
  // Delegated handler — survives sidebar re-renders.
  if (nav.dataset.navWired === '1') return;
  nav.dataset.navWired = '1';
  nav.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const btn = target?.closest<HTMLElement>('button[data-view]');
    if (!btn) return;
    const v = btn.dataset.view;
    if (v) void openView(v);
  });
}

async function ensureSeedAndStatus(): Promise<void> {
  const text = getEl('seedStatusText');
  const status = getEl('seedStatus');
  if (!window.IFI?.studio.db) return;
  const db = window.IFI.studio.db as {
    seedIfEmpty?: (cb: (m: string) => void) => Promise<number>;
  };
  try {
    const seeded = await db.seedIfEmpty?.((m) => {
      if (text) text.textContent = m;
    });
    if (status) status.classList.add('seed-status--ok');
    if (text)
      text.textContent =
        (seeded ?? 0) > 0 ? `✓ Seeded ${seeded} record` : `✓ DB siap`;
  } catch (e) {
    if (text) text.textContent = '⚠ Seed gagal: ' + (e as Error).message;
    if (status) status.classList.add('seed-status--error');
  }
}

/**
 * Per-role default landing destination (when no hash deep-link is present).
 * - super_admin / project_admin → Research Overview (workflow status at a glance)
 * - source_person → Data Collection Hub (their assigned tasks)
 * - viewer → Research Overview (read-only context)
 * Falls back to the first role-visible NAV_ITEM if the preferred target is unavailable.
 */
function defaultLandingForRole(role: Role): string {
  const preferred: Record<Role, string> = {
    super_admin: 'research-overview',
    project_admin: 'research-overview',
    source_person: 'data-collection-hub',
    viewer: 'research-overview',
  };
  const pref = preferred[role];
  const prefItem = pref ? findNavItem(pref) : undefined;
  if (prefItem && prefItem.viewId && prefItem.allowedRoles.includes(role)) return pref;
  const fallback = NAV_ITEMS.find((it) => it.viewId && it.allowedRoles.includes(role));
  return fallback?.id ?? 'research-overview';
}

async function bootstrap(): Promise<void> {
  wireNav();
  wireHashNav();
  await ensureSeedAndStatus();
  const role = auth.getRole();
  const hash = (location.hash || '').slice(1);
  const hashItem = hash ? findNavItem(hash) : undefined;
  const target = hashItem && hashItem.viewId && hashItem.allowedRoles.includes(role)
    ? hash
    : defaultLandingForRole(role);
  await openView(target);
}

/**
 * In-app deep links (e.g. `admin.html#triangulation-matrix` buttons rendered
 * inside view content) change `location.hash` but do NOT reload the SPA, so
 * bootstrap()'s one-time hash read never re-fires. This listener routes those
 * hash changes to openView(), making every `#<nav-id>` link clickable from
 * anywhere — and ignores non-nav hashes like `#reset`.
 */
function wireHashNav(): void {
  window.addEventListener('hashchange', () => {
    const hash = (location.hash || '').slice(1);
    if (!hash) return;
    const item = findNavItem(hash);
    const role = auth.getRole();
    if (item && item.viewId && item.allowedRoles.includes(role)) {
      void openView(hash);
    }
  });
}

function init(): void {
  wireLogin();
  document.querySelectorAll<HTMLElement>('[data-close-drawer]').forEach((b) =>
    b.addEventListener('click', () => {
      const dr = getEl('assetDrawer');
      if (dr) dr.hidden = true;
    }),
  );
  void (async () => {
    const authed = await auth.isAuthedAsync();
    if (authed) {
      await showApp();
    } else {
      showLogin();
    }
  })();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
